import type { BadgeOffer } from "./badge/client.ts";
import type { TravelEvent } from "./travel/wire.ts";
import type { HarvestStore } from "./harvest/store.ts";
import type { CollisionGrid, CreatureLook, Direction, FeedbackAccessState, HandoffCause, HeldLooks, NpcLook, PartyPositionsRecord, PartyVitalsRecord, WireLook } from "odyssey-shared";
import type { SoundCues } from "./audio/cues.ts";
import type { ArrangedBar } from "./bar/arranged.ts";
import type { ToolBelt } from "./belt/store.ts";
import type { CompanionStore } from "./companion/store.ts";
import type { BarStore } from "./bar/store.ts";
import type { BagStore } from "./bag/store.ts";
import type { CharacterFigure } from "./character.ts";
import type { ChatFeed } from "./chat/events.ts";
import type { ChestStore } from "./chest/store.ts";
import type { GainedEvent } from "./chest/wire.ts";
import type { GameConnection, Interaction } from "./connection.ts";
import type { DeathScreen } from "./death/death-screen.ts";
import type { SignInEnd } from "./disconnect.ts";
import type { EmoteFeed, ShownEmoji } from "./emotes/wire.ts";
import type { Fight } from "./fight/fight.ts";
import type { StanceStore } from "./fight/stance.ts";
import type { GroundSurfaces } from "./ground-surfaces.ts";
import type { PageGround } from "./ground/page-ground.ts";
import type { GroundView } from "./ground/view.ts";
import type { MaintenanceNotice } from "./hud/maintenance-warning.ts";
import type { LevelTracker } from "./hud/level-up.ts";
import type { VfxPlayer } from "./vfx/player.ts";
import type { HudFeed } from "./hud/store.ts";
import type { TargetLookup } from "./interact-target.ts";
import type { JoinAwait } from "./join.ts";
import type { LeaveAnswer } from "./leave-desk.ts";
import type { LoadingEvent, LoadingFailure } from "./loading-state.ts";
import type { LocalPlayer } from "./local-player.ts";
import type { MessageLine } from "./message-line.ts";
import type { FrozenGate } from "./moderation/frozen-gate.ts";
import type { ModerationFeed } from "./moderation/feed.ts";
import type { PageTargets } from "./page-targets.ts";
import type { PresenceProps } from "./presence.ts";
import type { ReconnectDeps } from "./reconnect.ts";
import type { ReadingFeed } from "./reading/store.ts";
import type { DialogueFeed } from "./dialogue/store.ts";
import type { RemoteCharacters } from "./remote-characters.ts";
import type { PageRun } from "./run/page.ts";
import type { WalletStore } from "./wallet/store.ts";
import type { CharacterStore } from "./character/store.ts";
import type { WorldMapSession } from "./world-map/session.ts";
import type { ProjectileView } from "./world/projectile-view.ts";
import type { WorldThings } from "./world-things.ts";
import type { BookMap } from "./neighbours/edge-book.ts";

/**
 * What a refresh made before a join came to: the token holds (`ready`), the gateway did not answer (`failed`), or the
 * sign-in ended, because the gateway refused to renew it (`expired`) or answered that the account needs a name first
 * (`naming`).
 */
export type FreshToken = "ready" | "failed" | SignInEnd;

/**
 * What the page hands the session. `token` gives the token to present in `hello`, read at each connection so a join
 * after a retry presents the newest one a kept-fresh session holds; `progress` is the loading shell's; `account` says
 * whether the token is an account session's, which a refusal tells the player to log in again for. `onAuthFailed` is
 * called when the gateway closes the socket because it refused the token presented in `hello`
 * (`CloseCode.AuthFailed`), so a page playing an account session can forget it; a page without one leaves it out.
 * `hud` is the HUD's store (P1.9-T13), told the player's name and vitals. `chat` is the chat panel's (ADR-068), handed
 * the way to send on the current connection, its chat frames, and its end. `reading` is the reading panel's
 * (P1.9-T37), closed as a session ends. `maintenance` hears the maintenance schedule the gateway sends (ADR-078).
 * `feedback` hears the gateway's offer of both kinds of report and the account's access to feedback, with the time a
 * pause has left (ADR-081's amendment of 2026-10-05).
 * `moderation` hears the moderation messages and the connection's end, and words the notice of a close with 4008
 * (ADR-056's amendment of 2026-10-04). `bar`
 * is the ability bar's (P2.2-T4), told the player's vitals, effects, own acts' answers, selection and its target's
 * effects, and handed the way a click on a slot uses it. `bag` is the bag's (P2.8-T11, ADR-091), told the inventory
 * frames, handed the way to ask for a use, and forgotten as a session ends. `chest` is the chest the loot window shows
 * (P2.8-T16, ADR-091 decisions 19 and 22), told the chest frames and each change of the bag, handed the way to ask for a
 * take and the way to tell the server the player closed its window (an `interact` with the verb `shut`, ADR-091's amendment of 2026-10-06), closed as the character dies, and forgotten as a session ends, so no window outlives its session; what it says goes to the message line, and
 * each line it hears taken to `loot`, the loot toasts, which also take each `lootGained` of a creature's drop (P2.8-T62) and its copper (P2.8-T39), cleared as a session ends. `levelUp` is the level-up toast (P2.4-T4), shown for a level
 * reached and cleared as a session ends. `discoveries` is the discovery toasts (P2.11-T8, ADR-096 decision 15), shown
 * for a shrine the page's character has just discovered, with a chat line and the stinger, and cleared as a session
 * ends. `sounds` plays the fight's, a level's and the drawn characters' actions' sounds
 * (ADR-085). `stance` is the stance the HUD's button draws and toggles (P2.2-T13); without one the session keeps its
 * own. `death` is the death screen (ADR-073's amendment "the respawn is the player's,
 * and a death is said in chat"), told of the player's own death, its cause, what it cost (the penalties of each `effects` frame, P2.5-T7), the respawn
 * offered and its stand, and
 * handed the way to ask for the respawn.
 */
export interface SessionOptions {
  readonly url: string;
  readonly token: () => string;
  readonly latencyMs: number;
  /**
   * A development page's artificial wait, in milliseconds, before the frames held across a handoff are read (`?handoffDelay=`), to look at the
   * slow transfer; absent or 0 on every other page.
   */
  readonly handoffDelayMs?: number;
  readonly progress: (event: LoadingEvent) => void;
  readonly account: boolean;
  readonly onAuthFailed?: () => void;
  /**
   * Makes sure the token `token` gives has not lapsed before a connection presents it (P1.9-T32): `"ready"` at once while
   * it holds, or a promise of what a refresh came to when it has lapsed in an outage, the refresh made once. `"failed"`
   * is a refresh the gateway did not answer, which the page does not present a lapsed token after, since `hello` would
   * end the sign-in; `"expired"` and `"naming"` are a sign-in that ended, as `endSignIn` takes it. A page with no account session leaves it out.
   */
  readonly freshToken?: () => "ready" | Promise<FreshToken>;
  /**
   * The timer and the draw the background reconnect runs on, the page's own when left out; a test hands in its own.
   */
  readonly reconnectTiming?: Partial<Pick<ReconnectDeps, "schedule" | "random" | "now">>;
  readonly hud: HudFeed;
  readonly chat?: ChatFeed;
  /** The emote wheel's store (ADR-114), handed the account's emote sets, the way to send and the connection's end. */
  readonly emotes?: EmoteFeed;
  /**
   * Hears each emoji, and each action's picture, the map server shows over a character in view (ADR-114 decision 6.5
   * and its amendment of 2026-10-09), for the canvas to draw.
   */
  readonly emoji?: (emoji: ShownEmoji) => void;
  readonly reading: ReadingFeed;
  /**
   * The dialogue window (ADR-136 decision 5.6), fed each `dialogue` and `dialogueClose` and closed as a session ends;
   * a page without one shows no conversation.
   */
  readonly dialogue?: DialogueFeed;
  readonly maintenance?: { readonly receive: (notice: MaintenanceNotice) => void };
  readonly feedback?: { readonly offer: () => void; readonly access: (state: FeedbackAccessState, endsInMs: number) => void };
  readonly moderation?: ModerationFeed;
  /** Takes the gateway's `staffBadgeOffer` (ADR-112 decision 5.5), which makes `/badge` a command on this page. */
  readonly badge?: (offer: BadgeOffer) => void;
  /**
   * The page's run (ADR-116 decisions 8, 11 and 12), told the character's Stamina and death from each `vitals` and
   * that the world was loaded again, which turns a toggled run off; the scene asks it for each input whether the
   * player asks to run and gives it the keys its switch follows. Without it the page never runs.
   */
  readonly run?: PageRun;
  readonly bar?: BarStore;
  /**
   * The player's bar (P2.8-T29, ADR-121): fed each `bar`, sending each `barSet`, and forgotten as a session ends.
   */
  readonly arranged?: ArrangedBar;
  /**
   * The player's tool belt (ADR-142 decision 3b): fed each `toolBelt`, sending each `toolBeltSet`, and forgotten as a
   * session ends.
   */
  readonly belt?: ToolBelt;
  /**
   * The player's companion (ADR-142 decision 6): fed each `companionState`, sending each `companionSet`, and forgotten as a session ends.
   */
  readonly companion?: CompanionStore;
  readonly bag?: BagStore;
  /**
   * The page's wallet (P2.8-T38, ADR-124 decision 4), fed each `wallet` and forgotten as a session ends.
   */
  readonly wallet?: Pick<WalletStore, "receive" | "reset">;
  /**
   * The page's character window (ADR-138's amendment of 2026-10-31), fed each `attributes` and `attributeRefused` and
   * forgotten as a session ends.
   */
  readonly character?: Pick<CharacterStore, "receive" | "refused" | "resetSession">;
  /**
   * The page's waypoint travel (ADR-137 decisions 22 and 23), fed each `travelOffer` and `travelState` and forgotten as
   * a session ends.
   */
  readonly travel?: { readonly receive: (event: TravelEvent) => void; readonly reset: () => void };
  /**
   * The page's harvesting (ADR-142 decision 3), fed each `harvestState`, each chunk's `harvested` list and each
   * `harvestEvent`, and forgotten as a session ends.
   */
  readonly harvest?: Pick<HarvestStore, "receiveList" | "receiveEvent" | "receiveState" | "reset">;
  readonly chest?: ChestStore;
  readonly loot?: { readonly show: (item: string, quantity: number) => void; readonly gained: (items: GainedEvent["items"], lost: GainedEvent["lost"], coin: number) => void; readonly clear: () => void };
  readonly levelUp?: { readonly show: (level: number) => void; readonly clear: () => void };
  readonly discoveries?: { readonly show: (name: string, xp: number) => void; readonly clear: () => void };
  /**
   * The page's world map (P2.12-T6, ADR-100), fed each `mapPlaces` and the join's `explored` and forgotten as a session
   * ends.
   */
  readonly worldMap?: WorldMapSession;
  /**
   * The page's party (ADR-101), fed each `partyVitals`.
   */
  readonly party?: { readonly receiveVitals: (vitals: Readonly<PartyVitalsRecord>) => void };
  /**
   * The page's party positions (ADR-100 decisions 10 to 16), fed each `partyPositions` and emptied when a session ends,
   * so a change of map and a reconnect start with no mark.
   */
  readonly partyPositions?: { readonly receive: (positions: Readonly<PartyPositionsRecord>) => boolean; readonly reset: () => void };
  readonly sounds?: SoundCues;
  /**
   * Called once when the gateway closes the page with 4011, in the world, while loading or in the login queue, the
   * moment maintenance begins (P1.12-T11, P1.12-T12). Told whether the page had been answered on this connection
   * (in the world, welcomed or queued), which is when the start cue plays; a join refused during maintenance is told
   * false and holds the page's sound without the cue.
   */
  readonly onMaintenanceStart?: (answered: boolean) => void;
  readonly stance?: StanceStore;
  readonly death?: Pick<DeathScreen, "died" | "stood" | "cause" | "cost" | "offer" | "bindRespawn" | "reset">;
}

/**
 * The loaded world as a session uses it: its version, its tile size in pixels, the grid the local player predicts
 * against, what each tile's ground is made of, the things drawn, the presence props and the target lookup, each
 * reset as a session starts or ends, and the shots drawn, which take each `projectiles` frame and are emptied at every
 * welcome (ADR-095 decision 21), and the ground items drawn (P2.8-T65).
 */
export interface SessionWorld {
  readonly version: string;
  readonly looks: readonly CreatureLook[];
  readonly npcLooks: readonly NpcLook[];
  readonly tileSize: number;
  readonly grid: CollisionGrid;
  readonly surfaces: GroundSurfaces;
  readonly things: WorldThings;
  readonly presence: PresenceProps;
  readonly lookup: TargetLookup;
  readonly projectiles: Pick<ProjectileView, "receive" | "clear" | "onLanded">;
  readonly ground: GroundView;
  /** The id of the map stood on. */
  readonly mapId: string;
  /** The world manifest's maps, in its order, which edge views index (ADR-147 decision 7). */
  readonly maps: readonly BookMap[];
}

/**
 * What the session asks of the scene: the world once it has loaded, a new character sprite, the page's own character's
 * sprite and how far its art reaches above its feet when they differ from every other player's (a local trial look,
 * P1.9-T88), how far every other player's art reaches above its feet (the plain figure's when omitted), the way to
 * tell a figure the look its handle holds, or that it holds none (ADR-094 decision 6; a figure that is not composed
 * is left as it is), a new creature sprite for an appearance (null when the version's look table or the character manifest
 * lacks it) and how far that creature's art reaches above its feet (`creature-head.ts`), the same two for an NPC from
 * the version's NPC look table (ADR-136 decision 1), the game loop's time, where
 * an `interact`'s outcome goes, the message line a leave clears, and the movement intent the keys hold now, which
 * aims a displacement (`fight/fight.ts`).
 */
export interface SessionHost {
  readonly world: () => SessionWorld | null;
  readonly character: () => CharacterFigure;
  readonly ownCharacter?: () => CharacterFigure;
  readonly ownHead?: () => number;
  readonly playerHead?: () => number;
  readonly wear?: (figure: CharacterFigure, look: Readonly<WireLook> | null) => void;
  readonly creature: (appearance: number, handle: number) => CharacterFigure | null;
  readonly creatureHead: (appearance: number) => number;
  readonly npc: (appearance: number) => CharacterFigure | null;
  readonly npcHead: (appearance: number) => number;
  readonly now: () => number;
  readonly interaction: (interaction: Interaction) => void;
  readonly messageLine: MessageLine;
  readonly intent: () => Direction;
  /**
   * The scene's map stage, which a handoff to another map drives (ADR-122 decision 2.9); a host without it stays on the
   * map it booted.
   */
  readonly maps?: MapStage;
}

/**
 * What a handoff asks of the scene: the id of the map it stands on (null while it holds none), a change to another map of the
 * loaded world, and a fade of the whole page to black or back, each a promise that settles when it is done.
 */
export interface MapStage {
  readonly current: () => string | null;
  readonly enter: (mapId: string) => Promise<void>;
  readonly fade: (direction: "out" | "in", ms: number) => Promise<void>;
  /**
   * Whether the ground beside the map holds chunks of a neighbour, which a crossing to it needs no loading screen for
   * (ADR-147 decision 18).
   */
  readonly holds?: (mapId: string) => boolean;
  /** Boots a neighbour behind the world shown, with no loading screen, for a crossing's welcome to stand on. */
  readonly boot?: (mapId: string) => Promise<void>;
  /** Whether a neighbour `boot` was asked for stands ready. */
  readonly ready?: (mapId: string) => boolean;
  /**
   * Stands the scene on a booted neighbour in one step, the map left kept beside it, and answers what to add to a point
   * of the map left to have it in the new map's frame, in sixteenths; null when the map is not booted.
   */
  readonly cross?: (mapId: string) => { readonly dx: number; readonly dy: number } | null;
  /** Forgets a neighbour `boot` booted and outdates a boot still running, as a crossing that ends some other way does. */
  readonly drop?: () => void;
  /** Shows or hides a neighbour's ground, as `edgeNear` names its version (ADR-147 decision 26). */
  readonly showNeighbour?: (mapId: string, shown: boolean) => void;
}

/**
 * A handoff to another map in progress (ADR-106 decision 19): the map and its name, why the character moves, whether the
 * loading screen is showing because the handoff outlasted its first second, whether the new map's welcome has been
 * applied, from which the page draws the new map until it is ready to fade in, and whether it is a seamless crossing.
 */
export interface HandoffState {
  readonly map: string;
  readonly name: string;
  readonly cause: HandoffCause;
  shell: boolean;
  landed: boolean;
  /**
   * Whether the handoff is a seamless crossing (ADR-147 decision 18): no fade, no loading screen, the world kept and the
   * character still walking, until the new map's welcome moves the frame.
   */
  seamless: boolean;
}

/**
 * What a session knows of itself, shared by the modules that route its frames and wire its fight, targets and sends:
 * its handle (-1 outside a session), whether it has its welcome, whether it applied a snapshot since, whether the
 * world is shown, whether its sign-in ended, whether a `vitals` frame has arrived, and whether it is trying again in the
 * background after a close (P1.9-T32), the world still shown and held. One object made once; the
 * frame handlers read and write it in place, so no frame allocates.
 */
export interface SessionState {
  handle: number;
  welcomed: boolean;
  /** Whether the gateway has put the page in the login queue on this connection. */
  queued: boolean;
  snapshotSeen: boolean;
  revealed: boolean;
  signInEnded: boolean;
  vitalsSeen: boolean;
  reconnecting: boolean;
  /** The handoff to another map in progress, or null. */
  handoff: HandoffState | null;
}

/**
 * Whether the gateway had answered the page on its connection: it is in the world, was welcomed or sits in the login
 * queue. A page told 4011 in that state was closed out by maintenance's start and hears the start cue; one refused at
 * its first message was not.
 *
 * @param state The session's state.
 * @returns True when the connection had been answered.
 */
export function answered(state: SessionState): boolean {
  return state.revealed || state.welcomed || state.queued;
}

/**
 * Makes the state of a session that has not joined.
 *
 * @returns A state with no handle and every flag false.
 */
export function newSessionState(): SessionState {
  return { handle: -1, welcomed: false, queued: false, snapshotSeen: false, revealed: false, signInEnded: false, vitalsSeen: false, reconnecting: false, handoff: null };
}

/**
 * What the pieces a session builds first are made from: what the page handed it, what the scene gives it, its state,
 * the way to the current connection, the local player and the frozen gate.
 */
export interface SessionCore {
  readonly options: SessionOptions;
  readonly host: SessionHost;
  readonly state: SessionState;
  readonly connection: () => GameConnection | null;
  readonly local: LocalPlayer;
  readonly gate: FrozenGate;
}

/**
 * Everything the modules that route frames and wire sends read, once the session has built its parts: the core and
 * the other players' characters, the fight, the targets, the ground items, the held looks and the level tracker.
 */
export interface SessionParts extends SessionCore {
  readonly remotes: RemoteCharacters;
  readonly fight: Fight;
  readonly targets: PageTargets;
  readonly ground: PageGround;
  readonly looks: HeldLooks;
  readonly levels: LevelTracker;
  /** The vfx player the session's triggers call (ADR-134). */
  readonly vfx: VfxPlayer;
  readonly stance: StanceStore;
}

/**
 * The session's own moves that frame handling asks for: give a connection up with a failure, take a server's silence, forget the session that
 * ended, take a close, and take the gateway's answer to a chosen leave.
 */
export interface SessionLifetime {
  readonly giveUp: (failure: LoadingFailure) => void;
  readonly silent: (awaited: JoinAwait) => void;
  /** Forgets the map's session for a welcome or a handoff on the same socket, keeping the chat and emotes the gateway opened on it. */
  readonly reset: () => void;
  /**
   * Forgets what belonged to the map a seamless crossing leaves (ADR-147 decision 18, note 14): the handle-bound and
   * map-bound state only. The character, its prediction and sprite, the bag, bars, belt, wallet, character window,
   * companion, run, levels, chat and emotes carry on, since the next join's frames overwrite them in place.
   */
  readonly crossed: () => void;
  /** Gives a handoff that stalled up: the connection is dropped and the notice shows with Retry. */
  readonly stalled: (failure: LoadingFailure) => void;
  readonly close: (code: number, reason: string) => void;
  readonly leaveAnswered: (answer: LeaveAnswer) => void;
  /** Takes one keepalive round trip in milliseconds, which the connection mark judges for a slow link. */
  readonly roundTrip: (rttMs: number) => void;
}

